/**
 * The pick clock — one component, six placements (Pass 17 rev B, 17n).
 *
 * The same number was drawn six ways: the shell's strip, the board's clock bar, the confirm step,
 * the phone's pinned bar, Home's draft card and the wall. Six drawings of one number is where a
 * redesign drifts, so this is the one drawing, with a size and a skin, and the two kinds it can be:
 *
 *   LEFT     a filled chip with a draining bar under it. Amber under thirty seconds, red and the
 *            word TIME once the allowance is spent. The bar is the tell at a glance.
 *   ELAPSED  a hollow chip with a `+` prefix and no bar. A round with no announced allowance
 *            (`pickSeconds()` returns null) can never fake urgency: nothing drains, nothing turns
 *            amber, and the `+` is the tell up close.
 *
 * Which kind is derived, never chosen: `seconds` is the round's allowance or null, and that is
 * the whole decision. The placements that have no allowance to hand (the strip, which reads only
 * a `since`) get the elapsed kind by construction.
 *
 * It owns its own one-second timer, like `ElapsedClock` before it, so a tick repaints one span
 * rather than the board around it.
 */

import { useEffect, useState } from 'react';
import { countdown } from '../domain/clocks.js';
import { elapsed } from '../domain/draftStrip.js';

export type PickClockSize = 's' | 'm' | 'l';
export type PickClockSkin = 'dark' | 'light';

interface Props {
  /** When the pick went on the clock — the previous pick's timestamp, or the restart. */
  since: string | null | undefined;
  /** The round's allowance in seconds, or null where the commissioner set none. */
  seconds: number | null | undefined;
  size?: PickClockSize;
  skin?: PickClockSkin;
  className?: string;
  /** For tests and the wall's sweep: a fixed instant instead of the live one. */
  now?: number;
}

/** Under this many seconds the chip goes amber. */
export const URGENT_UNDER = 30;

export function PickClock({ since, seconds, size = 'm', skin = 'light', className = '', now: fixed }: Props) {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (fixed !== undefined || !since) return undefined;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since, fixed]);
  const now = fixed ?? tick;

  const left = seconds ? countdown(since, seconds, now) : null;
  if (left) {
    const frac = Math.max(0, Math.min(1, left.left / seconds!));
    const tone = left.over ? 'over' : left.left < URGENT_UNDER ? 'urgent' : 'calm';
    return (
      <span className={`pclock left ${size} ${skin} ${tone} ${className}`}
        title={left.over ? 'The pick clock has run out' : 'Time left on the pick clock'}
        role="timer" aria-live="off">
        <span className="pcfig">{left.over ? 'TIME' : left.text}</span>
        <span className="pcbar" aria-hidden="true"><i style={{ width: `${Math.round(frac * 100)}%` }} /></span>
      </span>
    );
  }
  const text = elapsed(since, now);
  if (!text) return null;
  return (
    <span className={`pclock elapsed ${size} ${skin} ${className}`}
      title="How long this pick has been on the clock — no allowance is set for this round"
      role="timer" aria-live="off">
      <span className="pcfig"><i aria-hidden="true">+</i>{text}</span>
    </span>
  );
}
